// src/frontend/js/modern/modules/multi-list/ui.js

import { BaseListUI } from '../../shared/base-list-ui.js';
import multiListModuleStore from './store.js';
import { destroyPopup } from '../../lib/popup-lite.js';
import { displayStore } from '../../core/storage/display-store.js';
import { normalizeItems} from "../../shared/store-helpers";
import fragmentManager from "../../core/fragment-manager";
import stateCache from "../../core/state-cache";

class MultiListUI extends BaseListUI {
	constructor() {
		super({
			moduleName: 'multiList',
			store: multiListModuleStore,
			selectors: {
				addButton: '.wlfmc-add-to-multi-list',
				listContainer: '.wlfmc-manage-lists',
				dropdown: '.wlfmc-dropdown-content',
			}
		});
		this._prevStates = [];
	}

	/**
	 * Reactive render for multi-list: syncs list-counter rows and the
	 * lists counter icon from the current StateCache snapshot.
	 */
	renderFromState() {
		this.syncListCounterRows();
		this.updateCounterIcon();
		this.updateListItemCounts();
		this.updateAllProductListCounts();
	}

	/**
	 * Update the counter icon for multi-list counters based on list count.
	 *
	 * Multi-list counters are rendered with data-counter-type="lists",
	 * which does not match the module name 'multiList'. Because of that,
	 * BaseListUI.updateCounter() skips these wrappers inside _forEachCounter.
	 *
	 * This dedicated method updates only the icon graphic and empty-state
	 * visibility for multi-list counters, keeping the change minimal and
	 * avoiding any side effects on other counter types.
	 */
	updateCounterIcon() {
		const count = this._getFreshListCount();
		document.querySelectorAll('.wlfmc-lists-counter-wrapper').forEach((wrapper) => {
			const config = this._getConfig(wrapper);
			if (!config) return;

			const iconEl = wrapper.querySelector(this._counterSelectors.icon);
			if (iconEl) {
				this._updateIconGraphic(iconEl, count > 0 ? config.hasItemsIcon : config.emptyIcon);
			}

			wrapper.classList.toggle('empty', count === 0);

			if (config.hideIfEmpty) {
				wrapper.style.display = count === 0 ? 'none' : '';
			}
		});
	}

	_getManageContainer() {
		return document.querySelector('#wlfmc-lists.wlfmc-lists');
	}

	_getFreshListCount() {
		const lists = multiListModuleStore.getUserLists();
		return Array.isArray(lists) ? lists.length : 0;
	}

	_ensureManageStructure(container) {
		if (!container) return null;
		const existing = container.querySelector('ul.wlfmc-manage-lists');
		if (existing) return existing;
		// Drop the empty-state block before restoring the standard layout.
		container.querySelector('.lists-empty')?.remove();
		// Rebuild the header row from its template (keeps localized labels).
		if (!container.querySelector('.create-new-list-desc')) {
			const headerTemplate = window.wp?.template?.('wlfmc-list-page-header');
			if (headerTemplate) {
				container.insertAdjacentHTML('beforeend', headerTemplate({}));
			}
		}
		// Recreate the lists container for the new row to attach to.
		const manage = document.createElement('ul');
		manage.className = 'wlfmc-manage-lists';
		container.appendChild(manage);
		return manage;
	}

	isManagePageEmpty() {
		const container = this._getManageContainer();
		if (!container || !container.querySelector('.lists-empty')) return false;
		return this._getFreshListCount() === 0;
	}

	renderMultiListPopup(popup, lists, productLists) {
		const listContainer = popup.querySelector('.list');
		if (!listContainer) return;
		listContainer.innerHTML = '';
		const emptyMsg = popup.querySelector('.list-empty');
		if (emptyMsg) emptyMsg.style.display = 'none';
		const noResults = popup.querySelector('.no-results-row');
		if (noResults) noResults.style.display = 'none';
		lists = multiListModuleStore.getUserLists(lists);
		if (!lists || lists.length === 0) {
			const emptyDiv = document.createElement('div');
			emptyDiv.className = 'list-empty';
			emptyDiv.textContent = window.wlfmc_l10n?.labels?.multi_list_empty_list_text || '';
			listContainer.appendChild(emptyDiv);
			const saveBtn = popup.querySelector('.wlfmc_add_to_multi_list');
			if (saveBtn) saveBtn.style.display = 'none';
			return;
		}
		const template = window.wp?.template?.('wlfmc-list-item');
		if (!template) return;
		this._prevStates = [];
		let html = '';
		lists.forEach(list => {
			const isOnList = productLists.includes(list.wishlist_id);
			this._prevStates.push(isOnList);
			const items = this._getListItems(list);
			html += template({
				wishlist_id: list.wishlist_id,
				wishlist_name: list.wishlist_name || '',
				wishlist_desc: this._formatListDesc(list),
				wishlist_image: list.wishlist_image || this._resolveListImage(items),
				wishlist_url: list.wishlist_url || '#',
				is_on_list: isOnList,
			});
		});
		listContainer.innerHTML = html;
		popup._wlfmcPrevStates = [...this._prevStates];
		const counter = popup.querySelector('.wlfmc-number');
		if (counter) counter.textContent = productLists.length;
		const saveBtn = popup.querySelector('.wlfmc_add_to_multi_list');
		if (saveBtn) {
			saveBtn.dataset.listIds = JSON.stringify(productLists);
			saveBtn.setAttribute('data-list-ids', JSON.stringify(productLists));
			saveBtn.style.display = '';
			saveBtn.disabled = true;
		}
	}

	_getListItems(list) {
		const raw = list?.wishlist_items ?? list?.items ?? [];
		return normalizeItems(raw);
	}

	_formatListDesc(list) {
		const items = this._getListItems(list);
		// FIX: Use items.length directly (even 0) instead of || fallback.
		const count = Array.isArray(items)
			? items.length
			: parseInt(list?.count || list?.wishlist_count || 0, 10);
		const desc = list?.wishlist_desc || list?.description || '';
		if (count > 0) {
			return desc ? `${count} items - ${desc}` : `${count} items`;
		}
		return desc;
	}

	getProductListsFromCache(productId) {
		const allLists = multiListModuleStore.getUserLists();
		const productList = [];
		if (!Array.isArray(allLists)) return [];
		allLists.forEach(list => {
			if (list.wishlist_items && Array.isArray(list.wishlist_items) &&
				list.wishlist_items.some(item => String(item.product_id) === String(productId))) {
				productList.push(list.wishlist_id);
			}
		});
		return productList;
	}

	updateProductListCount(productId) {
		const productLists = this.getProductListsFromCache(productId);
		const count = productLists.length;
		const countSelector = [
			`.wlfmc-addtomultilist a[data-product-id="${productId}"] .wlfmc-multi-list-count`,
			`.wlfmc-addtomergelists a[data-product-id="${productId}"] .wlfmc-multi-list-count`,
		].join(', ');
		document.querySelectorAll(countSelector).forEach(el => {
			if (count > 0) {
				el.textContent = String(count);
				el.style.display = '';
				el.classList.add('has-items');
			} else {
				el.textContent = '0';
				el.style.display = 'none';
				el.classList.remove('has-items');
			}
		});
		const triggerSelector = [
			`.wlfmc-addtomultilist a[data-product-id="${productId}"]`,
			`.wlfmc-addtomergelists a[data-product-id="${productId}"]`,
		].join(', ');
		document.querySelectorAll(triggerSelector).forEach(trigger => {
			const wrapper = trigger.closest('.wlfmc-add-to-multi-list');
			if (wrapper) {
				if (count > 0) {
					wrapper.classList.add('exists');
				} else {
					wrapper.classList.remove('exists');
				}
			}
		});
	}

	/**
	 * Reconcile ALL product-level multi-list count badges on the page.
	 * Called reactively whenever multiList state changes.
	 */
	updateAllProductListCounts() {
		const triggers = document.querySelectorAll(
			'.wlfmc-addtomultilist a[data-product-id], .wlfmc-addtomergelists a[data-product-id]'
		);
		triggers.forEach(trigger => {
			const productId = parseInt(trigger.dataset.productId, 10);
			if (productId) {
				this.updateProductListCount(productId);
			}
		});
	}

	updateAddButton(productId, listIds, data = {}) {
		if (!Array.isArray(listIds)) listIds = (listIds != null) ? [listIds] : [];
		const popup = document.getElementById('add_to_list_popup');
		if (popup) {
			const addBtn = popup.querySelector('.wlfmc_add_to_multi_list');
			const freshProductLists = this.getProductListsFromCache(productId);
			if (addBtn) {
				addBtn.dataset.listIds = JSON.stringify(freshProductLists);
				addBtn.setAttribute('data-list-ids', JSON.stringify(freshProductLists));
			}
			const numberEl = popup.querySelector('.wlfmc-number');
			if (numberEl) numberEl.textContent = listIds.length;
		}
		if (Array.isArray(listIds)) {
			listIds.forEach(listId => {
				const el = document.querySelector(`.wlfmc-add-to-multi-list[data-list-id="${listId}"]`);
				if (el) el.classList.add('exists');
			});
		}
	}

	updateListItemCounts() {
		const allLists = multiListModuleStore.lists.get();
		if (!Array.isArray(allLists)) return;
		allLists.forEach(list => {
			const listId = String(list.wishlist_id);
			const desc = this._formatListDesc(list);
			const items = Array.isArray(list.wishlist_items) ? list.wishlist_items : null;
			// FIX: Use items.length directly when available (even if 0).
			const count = items !== null
				? items.length
				: parseInt(list.count || list.wishlist_count || 0, 10);
			const popup = document.getElementById('add_to_list_popup');
			if (!popup) return;
			const row = popup.querySelector(`li[data-wishlist-id="${listId}"]`);
			if (!row) return;
			const descEl = row.querySelector('small, .list-desc');
			if (descEl) descEl.textContent = desc;
			const miniRow = document.querySelector(
				`.wlfmc-lists-counter-wrapper a.list-row[data-list-id="${listId}"]`
			);
			if (miniRow) {
				const descEl = miniRow.querySelector('.list-desc');
				if (descEl) descEl.textContent = desc;
				const countEl = miniRow.querySelector('.list-total-count');
				if (countEl) countEl.textContent = `${count} items`;
			}
		});
	}

	updateRemoveButton(productId, listId) {
		const el = document.querySelector(`.wlfmc-add-to-multi-list[data-list-id="${listId}"]`);
		if (el) el.classList.remove('exists');
	}

	updateListUI(data) {
		const listId = data.wishlist_id || data.id;
		if (!listId) return;
		const payload = {
			wishlist_id: listId,
			wishlist_name: data.name || data.wishlist_name || '',
			wishlist_desc: data.description || data.wishlist_desc || '',
			wishlist_desc_raw: data.wishlist_desc_raw || data.description || data.wishlist_desc || '',
			wishlist_image: data.wishlist_image || '',
			wishlist_url: data.wishlist_url || '#',
			wishlist_privacy: data.privacy !== undefined ? Number(data.privacy) : Number(data.wishlist_privacy || 0),
			is_on_list: !!data.is_on_list,
			pending: !!(data.pending || data._optimistic),
			lists_nonce: data.lists_nonce || '',
			delete_nonce: data.delete_nonce || '',
			privacy_nonce: data.privacy_nonce || '',
		};
		const renderInto = (templateId, container, insert = 'beforeend') => {
			if (!container) return null;
			const template = window.wp?.template?.(templateId);
			if (!template) return null;
			const html = template({ ...payload });
			container.insertAdjacentHTML(insert, html);
			return container.lastElementChild;
		};
		// 1) Checkbox items inside add/move/copy popups.
		['move_popup', 'copy_popup', 'add_to_list_popup'].forEach((popupId) => {
			const popupEl = document.getElementById(popupId);
			const container = popupEl?.querySelector('ul.list');
			if (container && !container.querySelector(`[data-wishlist-id="${listId}"]`)) {
				renderInto('wlfmc-list-item', container);
			}
		});
		// 2) Header searchable dropdown.
		const dropdown = document.getElementById('wlfmc_my_lists_dropdown');
		if (dropdown && !dropdown.querySelector(`[data-wishlist-id="${listId}"]`)) {
			renderInto('wlfmc-dropdown-item', dropdown);
			dropdown.closest('.wlfmc-select-list-wrapper')?.classList.remove('hide');
		}
		// 3) Manage-lists page item + its private edit/delete popups.
		const manage = this._ensureManageStructure(this._getManageContainer())
			|| document.querySelector(this._selectors.listContainer);
		if (manage) {
			const existingItem = manage.querySelector(`li[data-wishlist-id="${listId}"]`);
			if (!existingItem) {
				renderInto('wlfmc-manage-list-item', manage);
			} else if (!payload.pending) {
				// List item already exists (morphed from temp ID).
				// Patch its URL, nonces and remove pending state.
				this._patchManageListItem(existingItem, payload);
			}
			// Create edit/delete popups for confirmed lists.
			// Separated from item creation so popups are generated even
			// when the list row was morphed in place during optimistic create.
			if (!payload.pending) {
				if (!document.getElementById(`edit_popup_${listId}`)) {
					renderInto('wlfmc-edit-popup', document.body);
				}
				if (!document.getElementById(`delete_popup_${listId}`)) {
					renderInto('wlfmc-delete-popup', document.body);
				}
			}
		}
	}

	/**
	 * Patch an existing manage-list item after server confirmation.
	 * Updates URL, nonces and removes pending visual state.
	 * Called when the list row was morphed in place (temp → real ID)
	 * and needs its attributes synced with server data.
	 */
	_patchManageListItem(item, payload) {
		// Update list URL on all relevant links
		if (payload.wishlist_url && payload.wishlist_url !== '#') {
			item.querySelectorAll('a[href="#"], a.list-row, .wlfmc-open-list-link').forEach((link) => {
				link.setAttribute('href', payload.wishlist_url);
			});
		}
		// Update delete nonce
		if (payload.delete_nonce) {
			const deleteBtn = item.querySelector('.wlfmc-delete-list');
			if (deleteBtn) {
				deleteBtn.setAttribute('data-nonce', payload.delete_nonce);
			}
		}
		// Update privacy nonce
		if (payload.privacy_nonce) {
			const privacyBtn = item.querySelector('.wlfmc-toggle-privacy');
			if (privacyBtn) {
				privacyBtn.setAttribute('data-nonce', payload.privacy_nonce);
			}
		}
		// Update edit/save nonce
		if (payload.lists_nonce) {
			const saveBtn = item.querySelector('.wlfmc-save-list');
			if (saveBtn) {
				saveBtn.setAttribute('data-nonce', payload.lists_nonce);
			}
		}
		// Remove pending visual state
		item.classList.remove('wlfmc-pending');
	}

	/**
	 * Point-patch the per-row item counts after move/copy operations.
	 */
	patchListRowCount(listId, count) {
		document
			.querySelectorAll(`.list-row[data-list-id="${listId}"] .list-total-count`)
			.forEach((el) => { el.textContent = `${count} items`; });
	}

	removeListUI(listId) {
		this.syncListCounterRows();
		document.querySelectorAll(`[data-wishlist-id="${listId}"]`).forEach(el => el.remove());
		const dropdown = document.querySelector(this._selectors.dropdown);
		if (dropdown) {
			dropdown.querySelectorAll(`[data-wishlist-id="${listId}"]`).forEach(el => el.remove());
		}
		const editPopup = document.getElementById(`edit_popup_${listId}`);
		if (editPopup) {
			destroyPopup(editPopup);
		}
		const deletePopup = document.getElementById(`delete_popup_${listId}`);
		if (deletePopup) {
			destroyPopup(deletePopup);
		}
		// Counter rendering is handled reactively by renderFromState().
		this.reconcileListPagination();
	}

	reconcileListPagination() {
		const container = this._getManageContainer();
		if (!container) return;
		const perPage = parseInt(container.dataset.perPage || '10', 10) || 10;
		const currentPage = parseInt(container.dataset.currentPage || '1', 10) || 1;
		const totalLists = this._getFreshListCount();
		const listContainer = container.querySelector('ul.wlfmc-manage-lists');
		const visibleRows = listContainer
			? listContainer.querySelectorAll(':scope > li').length
			: 0;
		// Last list deleted: swap the structure for the empty-state template.
		// Never reload fragments or navigate in this case.
		if (totalLists === 0) {
			this._renderListsEmptyState(container);
			return;
		}
		// Defensive guard: every remaining list fits on the first page, yet
		// no row is rendered. Treat this as an empty page as well.
		if (visibleRows === 0 && currentPage === 1 && totalLists <= perPage) {
			this._renderListsEmptyState(container);
			return;
		}
		if (visibleRows === 0) {
			// Lists still exist on the server but pagination shifted
			// after deletions on a later page.
			if (currentPage > 1) {
				this._navigateToListPage(currentPage - 1);
			} else {
				fragmentManager.loadFragments();
			}
			return;
		}
		const totalPages = Math.max(1, Math.ceil(totalLists / perPage));
		if (currentPage > totalPages) {
			this._navigateToListPage(totalPages);
		}
	}

	_navigateToListPage(page) {
		const baseUrl = window.wlfmc_l10n?.lists_page_url || window.location.href;
		const url = new URL(baseUrl, window.location.origin);
		if (page <= 1) {
			url.searchParams.delete('pagenum');
		} else {
			url.searchParams.set('pagenum', String(page));
		}
		const container = document.getElementById('wlfmc-lists');
		if (container) container.dataset.currentPage = String(page);
		window.location.href = url.toString();
	}

	_renderListsEmptyState(container) {
		if (!container) return;
		// Tear down the standard structure pieces.
		container.querySelector('ul.wlfmc-manage-lists')?.remove();
		container.querySelector('nav')?.remove();
		const headerRow = container.querySelector('.create-new-list-desc')?.parentElement;
		if (headerRow) headerRow.remove();
		// Already in the empty state.
		if (container.querySelector('.lists-empty')) return;
		const template = window.wp?.template?.('wlfmc-list-page-empty');
		if (!template) return; // Empty-state template not available on this page.
		const holder = document.createElement('div');
		holder.innerHTML = template({});
		const emptyEl = holder.firstElementChild;
		if (emptyEl) container.appendChild(emptyEl);
	}

	updatePrivacyUI(listId, privacy) {
		const el = document.querySelector(`[data-wishlist-id="${listId}"] .wlfmc-toggle-privacy`);
		if (el) {
			el.dataset.privacy = privacy;
			el.setAttribute('data-privacy', String(privacy));
			const icon = el.querySelector('i');
			if (icon) icon.className = privacy === 1 ? 'wlfmc-icon-lock' : 'wlfmc-icon-unlock';
		}
	}

	/**
	 * Point-patch ALL DOM elements referencing a list's editable fields.
	 */
	refreshListDetails(listId, data) {
		const name = data.wishlist_name || '';
		const desc = data.wishlist_desc || '';
		const privacy = data.wishlist_privacy !== undefined ? Number(data.wishlist_privacy) : null;
		const list = multiListModuleStore.getList(listId);
		const combinedDesc = this._formatListDesc({
			wishlist_items: list?.wishlist_items,
			count: list?.count,
			wishlist_desc: desc,
		});
		// 1) Page header
		const headerIdEl = document.querySelector(
			`.wlfmc-list-actions-wrapper [data-wishlist-id="${listId}"]`
		);
		if (headerIdEl) {
			const wrapper = headerIdEl.closest('.wlfmc-list-actions-wrapper');
			if (wrapper) {
				const nameEl = wrapper.querySelector('.list-name');
				const descEl = wrapper.querySelector('.list-desc');
				if (nameEl) nameEl.textContent = name;
				if (descEl) descEl.textContent = desc;
			}
		}
		// 2) Manage-lists page rows
		document.querySelectorAll(
			`ul.wlfmc-manage-lists li[data-wishlist-id="${listId}"]`
		).forEach((li) => {
			const nameEl = li.querySelector('.list-name');
			const descEl = li.querySelector('.list-desc');
			if (nameEl) nameEl.textContent = name;
			if (descEl) descEl.textContent = desc;
		});
		// 3) Header dropdown rows
		document.querySelectorAll(
			`#wlfmc_my_lists_dropdown li[data-wishlist-id="${listId}"]`
		).forEach((li) => {
			const nameEl = li.querySelector('.list-name');
			const descEl = li.querySelector('.list-desc');
			if (nameEl) nameEl.textContent = name;
			if (descEl) descEl.textContent = combinedDesc;
		});
		// 4) Add/Move/Copy popup checkbox rows
		['add_to_list_popup', 'move_popup', 'copy_popup'].forEach((popupId) => {
			const popup = document.getElementById(popupId);
			if (!popup) return;
			const row = popup.querySelector(`li[data-wishlist-id="${listId}"]`);
			if (!row) return;
			const nameEl = row.querySelector('strong');
			const descEl = row.querySelector('small');
			if (nameEl) nameEl.textContent = name;
			if (descEl) descEl.textContent = combinedDesc;
		});
		// 5) Lists-counter mini rows
		document.querySelectorAll(
			`.wlfmc-lists-counter-wrapper a.list-row[data-list-id="${listId}"]`
		).forEach((row) => {
			const nameEl = row.querySelector('.list-name');
			const descEl = row.querySelector('.list-desc');
			if (nameEl) nameEl.textContent = name;
			if (descEl) descEl.textContent = desc;
		});
		// 6) Privacy icon + share button visibility
		if (privacy !== null) {
			this.updatePrivacyUI(listId, privacy);
			if (privacy === 0) {
				document.querySelectorAll(
					`.wlfmc-popup-trigger[data-popup-id="share_popup_${listId}"]`
				).forEach((el) => { el.style.display = ''; });
				document.querySelectorAll('.share-wrapper').forEach((el) => {
					el.style.display = '';
				});
			} else {
				document.querySelectorAll(
					`.wlfmc-popup-trigger[data-popup-id="share_popup_${listId}"]`
				).forEach((el) => { el.style.display = 'none'; });
				document.querySelectorAll('.share-wrapper').forEach((el) => {
					el.style.display = 'none';
				});
			}
		}
		// Counter rendering is handled reactively by renderFromState().
	}

	/**
	 * Sync ALL lists-counter mini rows with the current store state.
	 */
	/**
	 * Sync ALL lists-counter mini rows with the current store state.
	 * Single source of truth for list-counter DOM reconciliation.
	 */
	syncListCounterRows() {
		const allLists = multiListModuleStore.lists.get();
		if (!Array.isArray(allLists)) return;
		let lists = multiListModuleStore.getUserLists(allLists);
		lists = [...lists].sort((a, b) => {
			const countA = Array.isArray(a.wishlist_items) ? a.wishlist_items.length : 0;
			const countB = Array.isArray(b.wishlist_items) ? b.wishlist_items.length : 0;
			return countB - countA;
		});
		const processedParents = new Set();
		document.querySelectorAll('.wlfmc-lists-counter-wrapper').forEach((wrapper) => {
			const uniqueId = wrapper.dataset.id || wrapper.getAttribute('data-id');
			const contentArea = wrapper.querySelector('.wlfmc-counter-content');
			const perPage = parseInt(wrapper.dataset.perPage || wrapper.getAttribute('data-per-page') || '0', 10);
			const visibleLists = perPage > 0 ? lists.slice(0, perPage) : lists;
			const remainingRows = visibleLists.length;
			const shouldHide = wrapper.dataset.hideIfEmpty === '1' && remainingRows === 0;
			wrapper.style.display = shouldHide ? 'none' : '';
			if (!contentArea) return;
			if (remainingRows === 0) {
				contentArea.querySelectorAll('.wlfmc-view-lists-link').forEach(el => el.remove());
				const itemsWrap = contentArea.querySelector('.wlfmc-wishlist-items-wrapper');
				if (itemsWrap) itemsWrap.remove();
				if (!contentArea.querySelector('.wlfmc-lists-empty')) {
					const emptyTemplate = uniqueId ? window.wp?.template?.(`wlfmc-mini-list-empty-${uniqueId}`) : null;
					if (emptyTemplate) {
						const holder = document.createElement('div');
						holder.innerHTML = emptyTemplate({});
						if (holder.firstElementChild) contentArea.appendChild(holder.firstElementChild);
					}
				}
				return;
			}
			contentArea.querySelector('.wlfmc-lists-empty')?.remove();
			let itemsWrap = contentArea.querySelector('.wlfmc-wishlist-items-wrapper');
			if (!itemsWrap && uniqueId) {
				const structureTemplate = window.wp?.template?.(`wlfmc-mini-list-structure-${uniqueId}`);
				if (structureTemplate) {
					const holder = document.createElement('div');
					holder.innerHTML = structureTemplate({});
					while (holder.firstElementChild) {
						contentArea.appendChild(holder.firstElementChild);
					}
					itemsWrap = contentArea.querySelector('.wlfmc-wishlist-items-wrapper');
				}
			}
			if (!itemsWrap) return;
			// Patch existing rows
			visibleLists.forEach((list) => {
				const row = itemsWrap.querySelector(`.list-row[data-list-id="${list.wishlist_id}"]`);
				if (row) this._patchListCounterRow(row, list);
			});
			// Add new rows
			if (uniqueId) {
				const template = window.wp?.template?.(`wlfmc-mini-list-item-${uniqueId}`);
				if (template) {
					visibleLists.forEach((list) => {
						if (itemsWrap.querySelector(`.list-row[data-list-id="${list.wishlist_id}"]`)) return;
						const items = Array.isArray(list.wishlist_items) ? list.wishlist_items : [];
						const imageHtml = this._resolveListImage(items);
						const count = items.length || parseInt(list.count || list.wishlist_count || 0, 10);
						const holder = document.createElement('div');
						holder.innerHTML = template({
							wishlist_id: list.wishlist_id,
							wishlist_token: list.wishlist_token || '',
							wishlist_url: list.wishlist_url || '#',
							wishlist_image: imageHtml,
							wishlist_name: list.wishlist_name || '',
							wishlist_desc: list.wishlist_desc || '',
							wishlist_count: count,
							pending: list._optimistic === true,
						});
						const newRow = holder.firstElementChild;
						if (newRow) {
							const title = itemsWrap.querySelector('.wlfmc-counter-title');
							if (title) title.after(newRow);
							else itemsWrap.prepend(newRow);
						}
					});
				}
			}
			// Remove stale rows
			const visibleIds = new Set(visibleLists.map(l => String(l.wishlist_id)));
			itemsWrap.querySelectorAll('.list-row').forEach((row) => {
				if (!visibleIds.has(row.dataset.listId)) row.remove();
			});
			// Re-append rows in sorted order
			visibleLists.forEach((list) => {
				const row = itemsWrap.querySelector(`.list-row[data-list-id="${list.wishlist_id}"]`);
				if (row) itemsWrap.appendChild(row);
			});
		});
	}

	_patchListCounterRow(row, list) {
		const items = Array.isArray(list.wishlist_items) ? list.wishlist_items : null;
		// FIX: Use items.length directly when the array exists (even if 0).
		// The previous `items.length || list.count` treated 0 as falsy and
		// fell back to the stale server count after all items were removed.
		const count = items !== null
			? items.length
			: parseInt(list.count || list.wishlist_count || 0, 10);
		const countEl = row.querySelector('.list-total-count');
		if (countEl) {
			countEl.textContent = `${count} items`;
		}
		const imageHtml = this._resolveListImage(items || []);
		const flexContainer = row.querySelector(':scope > .d-flex');
		if (flexContainer) {
			const existingImg = flexContainer.querySelector(':scope > img');
			if (imageHtml) {
				const holder = document.createElement('div');
				holder.innerHTML = imageHtml;
				const newImg = holder.firstElementChild;
				if (existingImg && newImg) {
					existingImg.replaceWith(newImg);
				} else if (!existingImg && newImg) {
					flexContainer.insertBefore(newImg, flexContainer.firstChild);
				}
			} else if ((!items || !items.length) && existingImg) {
				existingImg.remove();
			}
		}
		if (list.wishlist_url) {
			row.setAttribute('href', list.wishlist_url);
		}
		// Manage pending state based on optimistic flag
		if (list._optimistic === true) {
			row.classList.add('wlfmc-pending');
		} else {
			row.classList.remove('wlfmc-pending');
		}
	}

	_resolveListImage(items, position = 'last') {
		if (!items || items.length === 0) return '';
		const item = 'first' === position ? items[0] : items[items.length - 1];
		if (!item || item.product_id === undefined) return '';
		const pid = item.product_id;
		let display = displayStore.get('multiList', pid);
		if (!display) {
			display = displayStore.get('wishlist', pid);
		}
		return display?.image || '';
	}
}

export const multiListUI = new MultiListUI();
export default multiListUI;
